/**
 * CreateListSheet — center modal with a name input + Create button.
 * Reused by TaskListAddButton and MoveToListModal's "Add new list" row.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { TASK_LIST_NAME_MAX } from '@/types/task-lists';

type CreateListSheetProps = {
  visible: boolean;
  onDismiss: () => void;
  onSubmit: (name: string) => Promise<void>;
};

export function CreateListSheet({ visible, onDismiss, onSubmit }: CreateListSheetProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<TextInput>(null);

  const scale = useSharedValue(0.95);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (visible) {
      scale.value = withTiming(1, { duration: 150 });
      opacity.value = withTiming(1, { duration: 150 });
      setName('');
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 120);
    } else {
      scale.value = withTiming(0.95, { duration: 150 });
      opacity.value = withTiming(0, { duration: 150 });
    }
  }, [visible, scale, opacity]);

  const containerStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const backdropStyle = useAnimatedStyle(() => ({ opacity: opacity.value * 0.5 }));

  const handleSubmit = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      onDismiss();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [name, submitting, onSubmit, onDismiss]);

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onDismiss}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onDismiss} />
      </Animated.View>
      <View style={styles.centered} pointerEvents="box-none">
        <Animated.View
          style={[
            styles.card,
            { backgroundColor: palette.cardBackground },
            containerStyle,
          ]}
        >
          <Text style={[styles.title, { color: palette.text }]}>New list</Text>
          <TextInput
            ref={inputRef}
            value={name}
            onChangeText={setName}
            maxLength={TASK_LIST_NAME_MAX}
            placeholder="List name"
            placeholderTextColor={palette.textMuted}
            autoCapitalize="words"
            returnKeyType="done"
            onSubmitEditing={handleSubmit}
            style={[
              styles.input,
              { color: palette.text, borderColor: palette.borderMuted },
            ]}
          />
          {error && <Text style={[styles.error, { color: palette.danger }]}>{error}</Text>}
          <View style={styles.row}>
            <TouchableOpacity onPress={onDismiss} style={styles.buttonGhost}>
              <Text style={[styles.buttonText, { color: palette.textMuted }]}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleSubmit}
              disabled={!name.trim() || submitting}
              style={[
                styles.buttonPrimary,
                { backgroundColor: palette.tint, opacity: !name.trim() || submitting ? 0.5 : 1 },
              ]}
            >
              <Text style={[styles.buttonText, { color: '#fff' }]}>Create</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: '#000' },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  card: {
    width: 300,
    padding: Spacing.lg,
    borderRadius: BorderRadius.lg,
    gap: Spacing.md,
  },
  title: { fontSize: 17, fontWeight: '600' },
  input: {
    borderWidth: 1,
    borderRadius: BorderRadius.md,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    fontSize: 15,
  },
  error: { fontSize: 13 },
  row: { flexDirection: 'row', justifyContent: 'flex-end', gap: Spacing.sm },
  buttonGhost: { paddingHorizontal: Spacing.md, paddingVertical: Spacing.sm },
  buttonPrimary: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderRadius: BorderRadius.md,
  },
  buttonText: { fontSize: 15, fontWeight: '600' },
});
